// Phase 5.5 — Global store for the list of in-flight + recently-failed
// video drafts.
//
// This store is INDEPENDENT of `videoDraftStore` (which manages the SINGLE
// draft the user is currently looking at inside the modal). This store
// manages the LIST of ALL drafts the user has either renderizando (post-
// approve in-flight) or that just failed and we want to show as error
// cards in the gallery.
//
// Why two stores? Different responsibilities:
//
//   videoDraftStore — owns the modal-side state machine of one draft. It
//   knows about edits, regenerations, the preview UI. It polls aggressively
//   while the modal is open. It clears when the modal closes.
//
//   videoInFlightStore — owns the gallery-side list of cards (loading or
//   error) that show in-flight drafts and their failures. It polls every
//   10s and keeps polling even when the modal is closed. Tracks reference_ids
//   that "disappeared" from the in-flight list and looks them up individually
//   to detect failures (mirror of how creativeGenerationStore handles image
//   failures: card flips to error state, stays visible until refresh or
//   manual dismiss).
//
// The single source of truth is always the backend. The store does not
// coordinate with videoDraftStore — they don't talk to each other.

import { create } from "zustand";
import {
  getVideoDraft,
  listInFlightVideoDrafts,
} from "@/services/ads/videos/videoDraftService";
import type { VideoDraftResponse } from "@/services/ads/videos/videoDraft.types";

const POLL_INTERVAL_MS = 10_000;

let pollingInterval: number | null = null;

/**
 * In-memory entry for a draft we're tracking. Wraps the backend response
 * with a lightweight `displayStatus` so the UI can render error cards
 * without re-querying.
 *
 * `displayStatus`:
 *   - "in-flight" — backend says the draft is in a post-approve in-flight
 *     state (GENERATING_IMAGE, WAITING_SCENES, MERGING). Render as
 *     loading card with spinner.
 *   - "error"     — the draft was in flight before but is now in ERROR
 *     state. Render as error card. Stays in memory until refresh or
 *     until the user dismisses it (removeDraft).
 */
export interface InFlightDraftEntry {
  draft: VideoDraftResponse;
  displayStatus: "in-flight" | "error";
}

interface VideoInFlightState {
  /** Drafts currently being tracked (in-flight + recently-failed). */
  entries: InFlightDraftEntry[];
  /** First-load fetch in flight. Used to show a skeleton on cold start. */
  isLoading: boolean;
  /** Last error message from the backend (debug only — UI does not surface). */
  lastError: string | null;

  /**
   * Calls the backend, reconciles with the local list, follows up on
   * disappeared drafts to detect errors. Replaces the in-flight subset
   * with what the backend returned, marks disappeared drafts as ERROR
   * if the individual /draft/{ref} call confirms ERROR state, otherwise
   * removes them (FINISHED, REJECTED, or any non-terminal-non-in-flight).
   */
  loadFromBackend: () => Promise<void>;
  /** Starts the 10s polling interval. Idempotent — safe to call twice. */
  startPolling: () => void;
  /** Stops the polling interval. Safe to call when nothing is running. */
  stopPolling: () => void;
  /** Local removal — used when the user dismisses an error card. */
  removeDraft: (referenceId: string) => void;
  /** Hard reset — clears state, stops polling. */
  reset: () => void;
}

export const useVideoInFlightStore = create<VideoInFlightState>((set, get) => ({
  // Memory-only — no localStorage. Mirrors the image creatives pattern
  // (creativeGenerationStore) where failed cards live until refresh or
  // manual dismiss.
  entries: [],
  isLoading: false,
  lastError: null,

  async loadFromBackend() {
    const previousEntries = get().entries;
    set({ isLoading: true, lastError: null });

    const { res, error } = await listInFlightVideoDrafts();
    if (error || !res) {
      set({
        isLoading: false,
        lastError: error?.message ?? "Failed to load in-flight drafts",
      });
      return;
    }

    // Backend returned the current in-flight list (post-approve states only).
    // Build a fresh map of the in-flight ones.
    const incomingByRef = new Map<string, VideoDraftResponse>();
    for (const draft of res) {
      incomingByRef.set(draft.reference_id, draft);
    }

    // Detect drafts that we were tracking before (in-flight) but that
    // disappeared from the new list. They could have transitioned to
    // FINISHED, ERROR, or REJECTED. We need to find out which.
    const previouslyInFlight = previousEntries
      .filter((e) => e.displayStatus === "in-flight")
      .map((e) => e.draft.reference_id)
      .filter((ref) => !incomingByRef.has(ref));

    // For each disappeared draft, hit /draft/{ref} individually to see
    // if it ended in ERROR (we want to keep it as an error card) or in
    // a happy terminal state (FINISHED — drop it, the regular gallery
    // will show the final video).
    const disappearedResults = await Promise.all(
      previouslyInFlight.map(async (ref) => {
        const { res: detail } = await getVideoDraft(ref);
        return { ref, detail };
      })
    );

    // Reconcile:
    //  1. Start with the in-flight entries from the backend response.
    //  2. Append disappeared entries that ended in ERROR.
    //  3. Preserve any error entries from the previous state that the
    //     user has not dismissed yet (sticky errors until refresh).
    const nextEntries: InFlightDraftEntry[] = [];

    for (const draft of res) {
      nextEntries.push({ draft, displayStatus: "in-flight" });
    }

    for (const { ref, detail } of disappearedResults) {
      if (!detail) continue;
      // Backend status strings come from VIDEO_DRAFT_STATUS.* —
      // "error" or any explicit failure marker.
      if (detail.status === "error" || detail.error_message) {
        nextEntries.push({ draft: detail, displayStatus: "error" });
      }
      // FINISHED / REJECTED / DONE → drop, regular gallery shows them.
    }

    // Sticky errors from the previous state: if a draft was already
    // marked as error in our local entries and the user hasn't dismissed
    // it yet, keep it. (It's NOT in the current backend in-flight list
    // because errors aren't in-flight.)
    for (const prev of previousEntries) {
      if (prev.displayStatus !== "error") continue;
      const ref = prev.draft.reference_id;
      const alreadyIn = nextEntries.some(
        (e) => e.draft.reference_id === ref
      );
      if (!alreadyIn) {
        nextEntries.push(prev);
      }
    }

    set({ entries: nextEntries, isLoading: false });
  },

  startPolling() {
    // Idempotent: do not stack multiple intervals.
    if (pollingInterval !== null) return;
    pollingInterval = window.setInterval(() => {
      get().loadFromBackend();
    }, POLL_INTERVAL_MS);
  },

  stopPolling() {
    if (pollingInterval !== null) {
      window.clearInterval(pollingInterval);
      pollingInterval = null;
    }
  },

  removeDraft(referenceId: string) {
    const next = get().entries.filter(
      (e) => e.draft.reference_id !== referenceId
    );
    set({ entries: next });
  },

  reset() {
    if (pollingInterval !== null) {
      window.clearInterval(pollingInterval);
      pollingInterval = null;
    }
    set({ entries: [], isLoading: false, lastError: null });
  },
}));
